import { ThemeDefault } from '@paygreen/paygreen-ui';
import { useMediaQuery } from './hooks/useMediaQuery';
import dayjs from './dayjsHelper';
import { frLangLocale } from './constants';

/**
 * @description it gets value from dimensions constants from PG-UI theme
 */
const getNumberFromThemeValue = (value) => {
    return Number(value.slice(0, -2));
};

/**
 * @description it gets value from mediaQuery from PG-UI theme
 */
const getNumberFromMediaQuery = (themeQuery) => {
    return Number(themeQuery.replace(/[^.\d]/g, ''));
};

/**
 * @description to detect dynamically screen size above 960px width
 */
const IsDesktop = () => {
    const isDesktop = useMediaQuery(
        [`${ThemeDefault.screen.min.md}`],
        [true],
        false,
    );

    return isDesktop;
};

/**
 * @description to detect dynamically screen size above 1200px width
 */
const IsBigDesktop = () => {
    const isBigDesktop = useMediaQuery(
        [`${ThemeDefault.screen.min.lg}`],
        [true],
        false,
    );

    return isBigDesktop;
};

/**
 * @description to detect dynamically screen size above 549px and under 960px width
 */
const IsTablet = () => {
    const IsTablet = useMediaQuery(
        [`${ThemeDefault.screen.max.sm}`, `${ThemeDefault.screen.min.md}`],
        [false, false],
        true,
    );

    return IsTablet;
};

/**
 * @description to detect dynamically screen size under 549px width
 */
const IsMobile = () => {
    const isMobile = useMediaQuery(
        [`${ThemeDefault.screen.max.sm}`],
        [true],
        false,
    );

    return isMobile;
};

/**
 * @description to convert date for API compatibility
 * @param {string} date - DD/MM/YYYY format
 *
 */
const formatDateApi = (date) => {
    return dayjs(date, 'DD/MM/YYYY').format('YYYY-MM-DD');
};

/**
 * @description to convert date for API compatibility
 * @param {string} date - DD/MM/YYYY format
 *
 */
 const internationalFormatToCalendarFormat = (date) => {
    return dayjs(date, 'YYYY-MM-DD').format('DD/MM/YYYY');
};

/**
 * @description to convert timestamp received from API to filter with query paramaters by date
 * @param {Number} date - timestamp
 */
const timestampToInternationalFormat = (date) => {
    return dayjs.unix(date).format('YYYY-MM-DD');
};

/**
 * @description to convert timestamp received from API to 'DD/MM/YYYY'
 * @param {Number} date - timestamp
 */
const timestampToCalendarFormat = (date) => {
    return dayjs.unix(date).format('DD/MM/YYYY');
};

/**
 * @description to convert into date timestamp from API
 * @param {Number} date - timestamp
 */
const timestampToDate = (date) =>
    new Date(date * 1000).toLocaleDateString('FR', {
        hour12: true,
    });

/**
 * @description to convert into date (with hour specified) timestamp from API
 * @param {Number} date - timestamp
 */
const timestampToDateWithHour = (date) =>
    new Date(date * 1000).toLocaleString('en-GB', {
        hour12: false,
    });

/**
 * @description to convert ISO 8601 date to DD/MM/YYYY format
 * @param {string} date - ISO 8601 format
 *
 */
const formatISODate = (date) => {
    return dayjs(date).format('DD/MM/YYYY');
};

/**
 * @description to adjust value length and limit up to 3
 * @param {number} value
 */
const adjustDecimalNumber = (value) => {
    switch (true) {
        case value < 10:
            return Number(value.toFixed(2));
        case value < 100:
            return Number(value.toFixed(1));
        default:
            return Number(value.toFixed(0));
    }
};

/**
 * @description to convert carbon data received from API to the most accurate value and unit
 * @param {string | number} value amount of carbon received in ton
 * @param {boolean} hasZero to return 0 or '--' when needed for null values
 * @return {ConvertPriceValueToEurosModel} convertedData
 */
const convertCarbonValue = (value, hasZero) => {
    const newValue = Number(value);
    let convertedValue;
    let convertedUnit;

    switch (true) {
        case !newValue:
            convertedValue = hasZero ? 0 : '––';
            convertedUnit = hasZero ? 'g' : '';
            break;

        case newValue >= 1:
            convertedValue = adjustDecimalNumber(newValue);
            convertedUnit = 't';
            break;

        case newValue >= 0.001 && newValue < 1:
            convertedValue = adjustDecimalNumber(newValue * 1000);
            convertedUnit = 'kg';
            break;

        default:
            convertedValue = adjustDecimalNumber(newValue * 1000000);
            convertedUnit = 'g';
    }

    return { convertedValue, convertedUnit };
};

/**
 * @description to convert price carbon data received from API to the most accurate display and value in euros
 * @param {string | number} value price of carbon received in euros cents
 * @param {boolean} hasZero to return 0 or '--' when needed for null values
 * @return {ConvertPriceValueToEurosModel}
 */
const convertPriceValueToEuros = (value, hasZero) => {
    const newValue = Number(value);
    let convertedValue;
    let convertedUnit = '€';

    switch (true) {
        case newValue === null || newValue === 0:
            convertedValue = hasZero ? 0 : '––';
            convertedUnit = hasZero ? '€' : '';
            break;

        case newValue >= 1:
            convertedValue = (newValue / 100).toFixed(2);
            break;

        default:
            convertedValue = '0.01';
    }

    return { convertedValue, convertedUnit };
};

/**
 * @description to convert and calculate each data from carbon reports
 * @param {Array} data - object of data received from API
 * @param {string} mode - mode of conversion "emitted"|"offset"|"notOffset"|"offsetPercent"|"notOffsetPercent"|"offsetPercentbyThirdParty"|"offsetPercentbyShop" | "offsetPercentFromOtherVendor"
 */
const convertCarbonReports = (data, mode) => {
    const totalOffset =
        +data?.carbonOffset +
        +data?.carbonOffsetByThirdParty +
        +data?.carbonOffsetFromAnotherVendor;

    switch (mode) {
        case 'emitted':
            return convertCarbonValue(+data?.carbonEmitted, true);
        case 'offset':
            return convertCarbonValue(totalOffset, true);
        case 'offsetViaAPI':
            return convertCarbonValue(data?.carbonOffset, true);
        case 'notOffset':
            return convertCarbonValue(+data?.carbonEmitted - totalOffset, true);
        case 'offsetPercent':
            return data && +data.carbonEmitted
                ? Number(((totalOffset / +data.carbonEmitted) * 100).toFixed(2))
                : 0;
        // The part of offset carbon, offset by a third party (during transportation)
        case 'offsetPercentbyThirdParty':
            return data && +data.carbonOffsetByThirdParty
                ? Number(
                      (
                          (+data.carbonOffsetByThirdParty / totalOffset) *
                          100
                      ).toFixed(2),
                  )
                : 0;
        // The part of offset carbon, offset by the shop
        case 'offsetPercentbyShop':
            return data && +data.carbonOffset
                ? Number(((+data.carbonOffset / totalOffset) * 100).toFixed(2))
                : 0;
        // The part of offset carbon, offset from another vendor of carbon project (external)
        case 'offsetPercentFromOtherVendor':
            return data && +data.carbonOffsetFromAnotherVendor
                ? Number(
                      (
                          (+data.carbonOffsetFromAnotherVendor / totalOffset) *
                          100
                      ).toFixed(2),
                  )
                : 0;
        case 'notOffsetPercent':
            return data && +data.carbonEmitted
                ? Number(
                      ((1 - totalOffset / +data.carbonEmitted) * 100).toFixed(
                          2,
                      ),
                  )
                : 0;
        default:
            return null;
    }
};

/**
 * @description to convert any value to kg
 * @param {number} data - value data received from API
 * @param {string} unit - unit of the data received t | kg | g
 */
const convertToKg = (data, unit) => {
    switch (unit) {
        case 't':
            return data * 1000;
        case 'g':
            return data / 1000;
        default:
            return data;
    }
};

/**
 * @description to detect automatically if browser language is french and find proper value in data (based on API Green data model)
 * @param {object} i18n - language context from i18n
 * @param {string} valueName - name of data to detect in French/English
 */
const IsFrench = (i18n, valueName) => {
    return (
        valueName +
        (frLangLocale.includes(i18n.language) ? 'French' : 'English')
    );
};

/**
 * @description to adapt dynamically color props depending on status
 * @param {object} statusOptions
 * @param {string} status
 */
const getStatusColorProps = (statusOptions, status) => {
    return {
        colorPallet: statusOptions[status].colorPallet,
        colorStatus:
            statusOptions[status].colorPallet === 'status'
                ? statusOptions[status].color
                : null,
        colorWab:
            statusOptions[status].colorPallet === 'wab'
                ? statusOptions[status].color
                : null,
    };
};

/**
 * transform the amount in cents to normal
 * @param {number} amount
 * @param {boolean} withZero - to keep 0 after comma
 * @returns {number | string} will returns a number except to keep 0 will return a string
 */
const formatCentsMoney = (amount, withZero) =>
    withZero ? (amount / 100).toFixed(2) : Number((amount / 100).toFixed(2));

/**
 * @description to truncate too long value
 * @param {string} value
 * @param {number} limit
 */
const truncateTooLongName = (value, limit) => {
    return value?.length > limit ? value?.substring(0, limit) + '...' : value;
};

/**
 * @description apply debounce on a function
 * @param {function} fn 
 * @param {number} delay 
 */
const debounce = (fn, delay) =>  {
    let timer = null;
    return function () {
        let context = this,
            args = arguments;
        clearTimeout(timer);
        timer = setTimeout(function () {
            fn.apply(context, args);
        }, delay);
    };
}

export {
    getNumberFromThemeValue,
    getNumberFromMediaQuery,
    IsBigDesktop,
    IsDesktop,
    IsMobile,
    IsTablet,
    IsFrench,
    formatDateApi,
    formatISODate,
    internationalFormatToCalendarFormat,
    timestampToDate,
    timestampToInternationalFormat,
    timestampToDateWithHour,
    timestampToCalendarFormat,
    convertCarbonReports,
    convertPriceValueToEuros,
    convertCarbonValue,
    convertToKg,
    adjustDecimalNumber,
    getStatusColorProps,
    formatCentsMoney,
    truncateTooLongName,
    debounce
};
